import { useState } from 'react';
import axios from 'axios';
import {
  BookOpen, Zap, Send, Copy, CheckCircle, ChevronDown, ChevronUp,
  Sparkles, Linkedin, Clock, RefreshCw, BarChart2, TrendingUp, Target,
} from 'lucide-react';
import ContentVisual from '../components/ContentVisual';
import ModelSelector from '../components/ModelSelector';

const API = process.env.REACT_APP_BACKEND_URL;

// ── Content Styles (from user screenshots) ────────────────────────────────────
const CONTENT_STYLES = [
  { id: 'Thought-Provoking', emoji: '🧠', desc: 'Challenges intellect, encourages deep reflection' },
  { id: 'Investigative',     emoji: '🕵️', desc: 'In-depth fact-finding, exposing hidden truths' },
  { id: 'Breaking News',     emoji: '⚡', desc: 'Urgent news, recent or latest developments' },
  { id: 'Scientific',        emoji: '🔬', desc: 'Data-driven, analytical, objective' },
  { id: 'Elaborate (Tabloid)', emoji: '🎭', desc: 'Sensational, entertaining, often exaggerated' },
  { id: 'Conversational',    emoji: '💬', desc: 'Casual, relatable, like talking to a friend' },
  { id: 'Professional',      emoji: '💼', desc: 'Formal, business-oriented, industry-specific jargon' },
  { id: 'Humorous',          emoji: '😄', desc: 'Witty, funny, uses jokes and satire' },
  { id: 'Inspirational',     emoji: '💡', desc: 'Uplifting, motivational, encouraging' },
  { id: 'Controversial',     emoji: '🔥', desc: 'Provocative, polarizing, sparks debate' },
  { id: 'Neutral',           emoji: '⚖️', desc: 'Balanced, unbiased, presenting all sides equally' },
  { id: 'Authoritative',     emoji: '🔒', desc: 'Confident, expert-led, commanding' },
  { id: 'Emotional',         emoji: '❤️', desc: 'Passionate, heartfelt, appeals to feelings' },
  { id: 'Skeptical',         emoji: '🤨', desc: 'Questioning, doubtful, critically examining claims' },
  { id: 'Predictive',        emoji: '🔮', desc: 'Foresight-thinking, based on trend extrapolation' },
  { id: 'Visionary',         emoji: '🚀', desc: 'Forward-thinking, futuristic, focuses on innovation' },
  { id: 'Nostalgic',         emoji: '📖', desc: 'Reminiscent, looking back at past events or works' },
];

// ── Theme & Style Picker ──────────────────────────────────────────────────────
function ThemeStylePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = CONTENT_STYLES.find(s => s.id === value) || CONTENT_STYLES[0];

  return (
    <div>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="theme-style-toggle"
        className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all text-left"
        style={{
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <span className="text-lg leading-none flex-shrink-0">{selected.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-white">{selected.id}</div>
          <div className="text-[10px] truncate" style={{ color: 'var(--llmc-text-muted)' }}>{selected.desc}</div>
        </div>
        {open ? <ChevronUp size={14} style={{ color: 'var(--llmc-text-muted)', flexShrink: 0 }} />
               : <ChevronDown size={14} style={{ color: 'var(--llmc-text-muted)', flexShrink: 0 }} />}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          className="mt-1 rounded-xl overflow-hidden"
          style={{
            background: 'rgba(10,10,20,0.97)',
            border: '1px solid rgba(255,255,255,0.12)',
            maxHeight: 280,
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          }}
        >
          {CONTENT_STYLES.map(style => (
            <button
              key={style.id}
              type="button"
              onClick={() => { onChange(style.id); setOpen(false); }}
              data-testid={`style-${style.id.toLowerCase().replace(/\s/g, '-').replace(/[()]/g, '')}`}
              className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all hover:opacity-90"
              style={{
                background: value === style.id ? 'rgba(124,94,250,0.12)' : 'transparent',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
              }}
            >
              <span className="text-base leading-none w-6 flex-shrink-0">{style.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold" style={{ color: value === style.id ? '#a68eff' : 'rgba(255,255,255,0.8)' }}>
                  {style.id}
                </div>
                <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>{style.desc}</div>
              </div>
              {value === style.id && (
                <CheckCircle size={12} style={{ color: '#a68eff', flexShrink: 0 }} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function BlogPublisher() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [keyword, setKeyword] = useState('');
  const [industry, setIndustry] = useState('AI & Technology');
  const [targets, setTargets] = useState(['qlia', 'iconiqs']);
  const [postToSocial, setPostToSocial] = useState(true);
  const [contentStyle, setContentStyle] = useState('Thought-Provoking');
  const [executiveFormat, setExecutiveFormat] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState('');

  // LinkedIn direct publish
  const [liPosting, setLiPosting] = useState(false);
  const [liResult, setLiResult] = useState(null);
  // A/B Variant
  const [abLoading, setAbLoading] = useState(false);
  const [abVariant, setAbVariant] = useState(null);
  // SEO Analyzer
  const [seoLoading, setSeoLoading] = useState(false);
  const [seoAnalysis, setSeoAnalysis] = useState(null);
  const [showSeo, setShowSeo] = useState(false);
  // Full A/B Test
  const [abTestLoading, setAbTestLoading] = useState(false);
  const [abTestResult, setAbTestResult] = useState(null);
  // Schedule to all channels
  const [schedLoading, setSchedLoading] = useState(false);
  const [schedResult, setSchedResult] = useState(null);
  const [modelOverride, setModelOverride] = useState(null); // null = default judge model

  const publish = async () => {
    if (!title || !content) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/blog/publish`, {
        title, content, target_keyword: keyword, industry,
        target_ids: targets, post_to_social: postToSocial,
        content_style: contentStyle,
        executive_format: executiveFormat,
        ...(modelOverride ? { model_override: modelOverride } : {}),
      });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  };

  const publishToLinkedIn = async () => {
    if (!title || !content) return;
    setLiPosting(true);
    setLiResult(null);
    try {
      const postContent = result?.optimized?.optimized_content || content;
      const { data } = await axios.post(`${API}/api/linkedin/publish`, {
        title,
        content: postContent,
        post_type: 'post',
      });
      setLiResult(data);
    } catch (e) {
      setLiResult({ error: e?.response?.data?.detail || 'LinkedIn publish failed. Check your token in Settings → LinkedIn.' });
    } finally {
      setLiPosting(false);
    }
  };

  const generateAbVariant = async () => {
    if (!title || !content) return;
    setAbLoading(true);
    setAbVariant(null);
    try {
      const { data } = await axios.post(`${API}/api/blog/ab-variant`, {
        title, content: content.slice(0, 2000), industry, keyword,
      });
      setAbVariant(data);
    } catch (e) {
      alert(e?.response?.data?.detail || 'A/B generation failed');
    } finally {
      setAbLoading(false);
    }
  };

  const analyzeSEO = async () => {
    if (!content.trim()) return;
    setSeoLoading(true); setSeoAnalysis(null); setShowSeo(true);
    try {
      const { data } = await axios.post(`${API}/api/blog/seo-analyze`, {
        title, content: content.slice(0, 4000), target_keyword: keyword || title, industry,
      });
      setSeoAnalysis(data.analysis);
    } catch { alert('SEO analysis failed'); }
    setSeoLoading(false);
  };

  const generateFullABTest = async () => {
    if (!content.trim()) return;
    setAbTestLoading(true); setAbTestResult(null);
    try {
      const { data } = await axios.post(`${API}/api/blog/ab-test`, {
        title, content: content.slice(0, 2000), n_variants: 3, test_dimension: 'hook',
      });
      setAbTestResult(data.test);
    } catch { alert('A/B Test generation failed'); }
    setAbTestLoading(false);
  };



  const scheduleToAllChannels = async () => {
    if (!title || !content) return;
    setSchedLoading(true);
    setSchedResult(null);
    try {
      // Schedule 24h from now for each platform
      const base = new Date();
      base.setHours(base.getHours() + 24);
      const platforms = ['LinkedIn', 'Twitter/X', 'Instagram', 'TikTok'];
      const results = await Promise.allSettled(platforms.map((platform, i) => {
        const dt = new Date(base.getTime() + i * 30 * 60 * 1000);
        return axios.post(`${API}/api/scheduler/create`, {
          title,
          content: result?.optimized?.optimized_content || content,
          platform,
          post_type: platform === 'LinkedIn' ? 'post' : 'post',
          scheduled_at: dt.toISOString(),
          brand_id: 'default',
        });
      }));
      const ok = results.filter(r => r.status === 'fulfilled').length;
      setSchedResult({ scheduled: ok, total: platforms.length });
    } catch {
      setSchedResult({ error: 'Scheduling failed' });
    } finally {
      setSchedLoading(false);
    }
  };

  const copy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 2000);
  };

  const selectedStyle = CONTENT_STYLES.find(s => s.id === contentStyle);
  const socialPosts = result?.social_posts || {};

  return (
    <div className="h-full overflow-y-auto" style={{ background: 'var(--llmc-bg)' }}>
      <div className="max-w-4xl mx-auto p-6 flex flex-col gap-5">

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, #2193b0, #6dd5ed)' }}>
            <BookOpen size={20} className="text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="font-black text-white text-lg" style={{ letterSpacing: '-0.02em' }}>Blog Publisher</h1>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(124,94,250,0.15)', color: '#a68eff', border: '1px solid rgba(124,94,250,0.3)' }}>
                {selectedStyle?.emoji} {contentStyle}
              </span>
              {executiveFormat && (
                <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full"
                  style={{ background: 'rgba(0,212,184,0.1)', color: '#00d4b8', border: '1px solid rgba(0,212,184,0.25)' }}>
                  EXEC FORMAT
                </span>
              )}
            </div>
            <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
              SEO · AIO · LLMO Optimized → qlia.io + iconiqs.ai
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="llmc-card p-5 flex flex-col gap-4">

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold" style={{ color: 'var(--llmc-text-muted)' }}>Blog Title *</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              className="llmc-input px-3 py-2.5 text-sm"
              placeholder="e.g. 10 AI Cybersecurity Threats You Can't Ignore in 2026"
              data-testid="blog-title-input" />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold" style={{ color: 'var(--llmc-text-muted)' }}>Content *</label>
            <textarea value={content} onChange={e => setContent(e.target.value)} rows={7}
              className="llmc-input px-3 py-2.5 text-sm resize-none"
              placeholder="Paste your full blog content here..."
              data-testid="blog-content-input" />
          </div>

          {/* Theme & Style + Keyword row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold" style={{ color: 'var(--llmc-text-muted)' }}>Theme & Style</label>
              <ThemeStylePicker value={contentStyle} onChange={setContentStyle} />
            </div>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold" style={{ color: 'var(--llmc-text-muted)' }}>Focus Keyword</label>
                <input value={keyword} onChange={e => setKeyword(e.target.value)}
                  className="llmc-input px-3 py-2 text-sm" placeholder="AI cybersecurity 2026"
                  data-testid="blog-keyword-input" />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold" style={{ color: 'var(--llmc-text-muted)' }}>Industry</label>
                <input value={industry} onChange={e => setIndustry(e.target.value)}
                  className="llmc-input px-3 py-2 text-sm" placeholder="AI & Technology"
                  data-testid="blog-industry-input" />
              </div>
            </div>
          </div>

          {/* Options row */}
          <div className="flex items-center gap-4 flex-wrap pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            {/* Executive Format toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <div
                className="relative w-8 h-4 rounded-full transition-colors flex-shrink-0"
                style={{ background: executiveFormat ? '#00d4b8' : 'rgba(255,255,255,0.1)' }}
                onClick={() => setExecutiveFormat(v => !v)}
              >
                <div className="absolute top-0.5 w-3 h-3 bg-white rounded-full shadow transition-transform"
                  style={{ left: executiveFormat ? '18px' : '2px' }} />
              </div>
              <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.7)' }}>
                Executive Format <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>(TL;DR + scannable headers)</span>
              </span>
            </label>

            {/* Publish targets */}
            <div className="flex items-center gap-2 ml-auto flex-wrap">
              {[{ id: 'qlia', label: 'qlia.io', color: '#7c5efa' }, { id: 'iconiqs', label: 'iconiqs.ai', color: '#00d4b8' }].map(t => (
                <button key={t.id} onClick={() => setTargets(prev => prev.includes(t.id) ? prev.filter(x => x !== t.id) : [...prev, t.id])}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                  style={{
                    background: targets.includes(t.id) ? `${t.color}18` : 'transparent',
                    border: `1px solid ${targets.includes(t.id) ? t.color + '66' : 'rgba(255,255,255,0.1)'}`,
                    color: targets.includes(t.id) ? t.color : 'rgba(255,255,255,0.4)',
                  }}>
                  {targets.includes(t.id) && <CheckCircle size={12} />} {t.label}
                </button>
              ))}
              <label className="flex items-center gap-1.5 text-xs cursor-pointer" style={{ color: 'rgba(255,255,255,0.5)' }}>
                <input type="checkbox" checked={postToSocial} onChange={e => setPostToSocial(e.target.checked)}
                  data-testid="social-posts-toggle" className="w-3.5 h-3.5 rounded" />
                Social posts
              </label>
            </div>
          </div>

          {error && (
            <div className="px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(235,87,87,0.1)', border: '1px solid rgba(235,87,87,0.3)', color: '#eb5757' }}>
              {error}
            </div>
          )}

          <div className="flex items-center gap-2">
            <button onClick={publish} disabled={loading || !title || !content}
              data-testid="publish-blog-btn"
              className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white text-sm transition-all llmc-btn-primary disabled:opacity-40">
              {loading
                ? <><div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} /> Optimizing & Publishing...</>
                : <><Sparkles size={16} /> Optimize & Publish</>}
            </button>
            <ModelSelector lab="blog" onChange={setModelOverride} />
          </div>

          {/* LinkedIn Direct Publish */}
          {(title && content) && (
            <div className="flex gap-2">
              <button onClick={publishToLinkedIn} disabled={liPosting}
                data-testid="publish-linkedin-btn"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all hover:scale-105 disabled:opacity-40"
                style={{ background: 'rgba(10,102,194,0.12)', border: '1px solid rgba(10,102,194,0.3)', color: '#0a66c2' }}>
                {liPosting
                  ? <><div className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#0a66c2', borderTopColor: 'transparent' }} /> Posting to LinkedIn...</>
                  : <><Linkedin size={13} /> Publish to LinkedIn</>}
              </button>
            </div>
          )}

          {liResult && (
            <div className={`text-xs px-3 py-2 rounded-lg ${liResult.error ? 'text-orange-400' : 'text-green-400'}`}
              style={{ background: liResult.error ? 'rgba(249,115,22,0.08)' : 'rgba(61,255,160,0.08)', border: `1px solid ${liResult.error ? 'rgba(249,115,22,0.2)' : 'rgba(61,255,160,0.2)'}` }}>
              {liResult.error || liResult.message}
              {liResult.post_url && <a href={liResult.post_url} target="_blank" rel="noreferrer" className="ml-2 underline" style={{ color: '#0a66c2' }}>View Post</a>}
            </div>
          )}

          {/* A/B Variant Generator */}
          {(title && content) && (
            <button onClick={generateAbVariant} disabled={abLoading}
              data-testid="generate-ab-btn"
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(166,142,255,0.1)', border: '1px solid rgba(166,142,255,0.25)', color: '#a68eff' }}>
              {abLoading ? <><RefreshCw size={12} className="animate-spin" /> Generating A/B...</> : <>✦ Generate A/B Variant</>}
            </button>
          )}
          {abVariant && (
            <div className="rounded-xl p-4 space-y-2" style={{ background: 'rgba(166,142,255,0.06)', border: '1px solid rgba(166,142,255,0.2)' }}>
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#a68eff' }}>A/B Variant</div>
              <div className="text-sm font-bold text-white">{abVariant.variant_title}</div>
              <div className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.7)' }}>{abVariant.variant_intro}</div>
              <div className="text-[10px] italic" style={{ color: 'rgba(255,255,255,0.4)' }}>{abVariant.rationale}</div>
              <button onClick={() => { setTitle(abVariant.variant_title); setContent(prev => abVariant.variant_intro + '\n\n' + prev.split('\n\n').slice(1).join('\n\n')); }}
                className="text-[10px] px-3 py-1 rounded-lg font-semibold"
                style={{ background: 'rgba(166,142,255,0.15)', color: '#a68eff' }}>
                Use this variant
              </button>
            </div>
          )}

          {/* SEO Analyzer */}
          {(title && content) && (
            <button onClick={analyzeSEO} disabled={seoLoading} data-testid="seo-analyze-btn"
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(0,212,184,0.08)', border: '1px solid rgba(0,212,184,0.25)', color: '#00d4b8' }}>
              {seoLoading ? <><RefreshCw size={12} className="animate-spin" /> Analyzing SEO...</> : <><TrendingUp size={12} /> SEO + LLMO Analyzer</>}
            </button>
          )}
          {showSeo && seoAnalysis && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: 'rgba(0,212,184,0.05)', border: '1px solid rgba(0,212,184,0.2)' }}>
              <div className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#00d4b8' }}>SEO + LLMO Analysis</div>
              {/* Scores */}
              <div className="grid grid-cols-5 gap-2">
                {Object.entries(seoAnalysis.scores || {}).map(([k, v]) => (
                  <div key={k} className="text-center rounded-xl p-2" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    <div className="text-lg font-black" style={{ color: v >= 80 ? '#3dffa0' : v >= 60 ? '#f97316' : '#ff5050' }}>{v}</div>
                    <div className="text-[9px] uppercase" style={{ color: 'rgba(255,255,255,0.4)' }}>{k}</div>
                  </div>
                ))}
              </div>
              {/* Top Fix */}
              {seoAnalysis.top_fix && (
                <div className="text-xs p-3 rounded-xl" style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', color: '#f97316' }}>
                  <strong>Top Fix:</strong> {seoAnalysis.top_fix}
                </div>
              )}
              {/* Issues */}
              {seoAnalysis.issues?.length > 0 && (
                <div className="space-y-1.5">
                  {seoAnalysis.issues.map((issue, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold flex-shrink-0"
                        style={{ background: issue.severity === 'high' ? 'rgba(255,80,80,0.15)' : issue.severity === 'medium' ? 'rgba(249,115,22,0.15)' : 'rgba(255,255,255,0.06)', color: issue.severity === 'high' ? '#ff5050' : issue.severity === 'medium' ? '#f97316' : 'rgba(255,255,255,0.4)' }}>
                        {issue.severity}
                      </span>
                      <div><span className="font-semibold text-white">{issue.area}:</span> <span style={{ color: 'rgba(255,255,255,0.6)' }}>{issue.issue}</span> → <span style={{ color: '#00d4b8' }}>{issue.fix}</span></div>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-3 text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                <span>{seoAnalysis.word_count} words</span>
                <span>{seoAnalysis.estimated_read_time}</span>
                <span>{seoAnalysis.grade_level}</span>
              </div>
            </div>
          )}

          {/* Full A/B Test */}
          {(title && content) && (
            <button onClick={generateFullABTest} disabled={abTestLoading} data-testid="full-ab-test-btn"
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(124,94,250,0.08)', border: '1px solid rgba(124,94,250,0.2)', color: '#a68eff' }}>
              {abTestLoading ? <><RefreshCw size={12} className="animate-spin" /> Generating A/B Test...</> : <><Target size={12} /> Full A/B Test (3 variants)</>}
            </button>
          )}
          {abTestResult && (
            <div className="rounded-xl p-4 space-y-3" style={{ background: 'rgba(124,94,250,0.05)', border: '1px solid rgba(124,94,250,0.2)' }}>
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#a68eff' }}>A/B Test — {abTestResult.test_dimension}</div>
              {abTestResult.winner_prediction && (
                <div className="text-xs p-3 rounded-xl" style={{ background: 'rgba(61,255,160,0.06)', color: '#3dffa0' }}>
                  Predicted winner: {abTestResult.winner_prediction}
                </div>
              )}
              <div className="space-y-2">
                {(abTestResult.variants || []).map((v, i) => (
                  <div key={i} className="rounded-xl p-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-black text-white">Variant {v.id}</span>
                      <span className="text-[10px]" style={{ color: '#a68eff' }}>{v.label}</span>
                      {v.predicted_ctr && <span className="ml-auto text-[10px]" style={{ color: '#3dffa0' }}>CTR: {v.predicted_ctr}</span>}
                    </div>
                    {v.headline && <div className="text-xs font-semibold text-white mb-1">{v.headline}</div>}
                    <div className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{v.opening}</div>
                    {v.best_for && <div className="text-[10px] mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Best for: {v.best_for}</div>}
                    <button onClick={() => { if (v.headline) setTitle(v.headline); if (v.opening) setContent(c => v.opening + '\n\n' + c.split('\n\n').slice(1).join('\n\n')); }}
                      className="mt-2 text-[10px] px-3 py-1 rounded-lg font-semibold hover:opacity-80"
                      style={{ background: 'rgba(166,142,255,0.12)', color: '#a68eff' }}>
                      Use Variant {v.id}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Schedule to All Channels */}
          {(title && content) && (
            <button onClick={scheduleToAllChannels} disabled={schedLoading}
              data-testid="schedule-all-btn"
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-bold transition-all hover:scale-105 disabled:opacity-40"
              style={{ background: 'rgba(255,110,180,0.1)', border: '1px solid rgba(255,110,180,0.25)', color: '#ff6eb4' }}>
              {schedLoading ? <><RefreshCw size={12} className="animate-spin" /> Scheduling...</> : <><Clock size={12} /> Schedule to All Channels</>}
            </button>
          )}
          {schedResult && !schedResult.error && (
            <div className="text-xs px-3 py-2 rounded-lg" style={{ background: 'rgba(61,255,160,0.06)', border: '1px solid rgba(61,255,160,0.2)', color: '#3dffa0' }}>
              Scheduled to {schedResult.scheduled}/{schedResult.total} platforms — check Post Scheduler for details
            </div>
          )}
        </div>

        {/* Results */}
        {result?.optimized && (
          <div className="llmc-card p-5 flex flex-col gap-4">
            <h2 className="font-bold text-sm text-white flex items-center gap-2">
              <Zap size={16} style={{ color: '#7c5efa' }} /> Optimization Results
            </h2>

            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Optimized Title', value: result.optimized.optimized_title, id: 'opt-title' },
                { label: 'Slug', value: result.optimized.slug, id: 'slug' },
                { label: 'Meta Description', value: result.optimized.meta_description, id: 'meta' },
                { label: 'Focus Keyword', value: result.optimized.focus_keyword, id: 'keyword' },
              ].map(({ label, value, id }) => value && (
                <div key={id} className="p-3 rounded-lg relative group" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <div className="text-[10px] mb-1" style={{ color: 'var(--llmc-text-muted)' }}>{label}</div>
                  <div className="text-xs text-white pr-6">{value}</div>
                  <button onClick={() => copy(value, id)} className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                    {copied === id ? <CheckCircle size={11} style={{ color: '#3dffa0' }} /> : <Copy size={11} style={{ color: 'rgba(255,255,255,0.4)' }} />}
                  </button>
                </div>
              ))}
            </div>

            {/* Formatted content preview */}
            {result.optimized.optimized_content && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold" style={{ color: 'var(--llmc-text-muted)' }}>
                    Optimized Content {executiveFormat ? '(Executive Format)' : ''}
                  </div>
                  <button onClick={() => copy(result.optimized.optimized_content, 'content')}
                    className="flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg transition-all"
                    style={{ background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>
                    {copied === 'content' ? <><CheckCircle size={10} style={{ color: '#3dffa0' }} /> Copied!</> : <><Copy size={10} /> Copy</>}
                  </button>
                </div>
                <div className="p-4 rounded-xl text-xs leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto"
                  style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)' }}>
                  {result.optimized.optimized_content.slice(0, 1200)}
                  {result.optimized.optimized_content.length > 1200 && (
                    <span style={{ color: 'rgba(255,255,255,0.3)' }}> ... [truncated for preview — copy for full content]</span>
                  )}
                </div>
              </div>
            )}

            {/* Article Visual — Remotion Animation or Animated Infographic */}
            {result.optimized?.animation_storyboard && (
              <div data-testid="content-visual-section">
                <div className="text-xs font-semibold mb-2" style={{ color: 'var(--llmc-text-muted)' }}>
                  Article Visual:
                </div>
                <ContentVisual storyboard={result.optimized.animation_storyboard} />
              </div>
            )}

            {/* Publish results */}
            {result.publish_results && (
              <div>
                <div className="text-xs font-semibold mb-2" style={{ color: 'var(--llmc-text-muted)' }}>Publish Status:</div>
                <div className="flex flex-col gap-1.5">
                  {Object.entries(result.publish_results).map(([target, res]) => (
                    <div key={target} className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
                      style={{ background: res.success ? 'rgba(61,255,160,0.08)' : 'rgba(235,87,87,0.08)', border: `1px solid ${res.success ? 'rgba(61,255,160,0.3)' : 'rgba(235,87,87,0.3)'}` }}>
                      {res.success ? <CheckCircle size={12} style={{ color: '#3dffa0' }} /> : <span style={{ color: '#eb5757' }}>✕</span>}
                      <span className="font-semibold capitalize">{target}</span>
                      {res.url && <a href={res.url} target="_blank" rel="noreferrer" className="ml-auto text-[10px] underline" style={{ color: '#7c5efa' }}>{res.url}</a>}
                      {res.error && <span className="ml-auto text-[10px]" style={{ color: '#eb5757' }}>{res.error}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Social posts */}
            {Object.keys(socialPosts).length > 0 && (
              <div>
                <div className="text-xs font-semibold mb-2" style={{ color: 'var(--llmc-text-muted)' }}>Social Posts:</div>
                <div className="flex flex-col gap-2">
                  {Object.entries(socialPosts).map(([platform, text]) => text && (
                    <div key={platform} className="p-3 rounded-lg relative group" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div className="text-[10px] font-bold uppercase mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>{platform}</div>
                      <div className="text-xs text-white whitespace-pre-wrap leading-relaxed">{text}</div>
                      <button onClick={() => copy(text, platform)} className="absolute top-2 right-2 p-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: 'rgba(255,255,255,0.06)' }}>
                        {copied === platform ? <CheckCircle size={12} style={{ color: '#3dffa0' }} /> : <Copy size={12} style={{ color: 'var(--llmc-text-muted)' }} />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
